import {
  Array,
  Boolean,
  Literal,
  Null,
  Number,
  Record,
  Static,
  String,
  Undefined,
  Union,
  Unknown,
} from 'runtypes';

import { RGBColorRuntype } from './AccountRuntype';

const PaymentMethodType = Union(Literal('STRIPE'), Literal('PAYMENTSPRING'));

const GroupCriteria = Record({
  field_id: String.Or(Null).optional(),
  field_value: Unknown.optional(),
});

const Group = Record({
  id: String,
  title: String,
  fields: Array(String),
  criteria: GroupCriteria.Or(Null).optional(),
});

const baseFields = {
  id: String,
  name: String.optional(),
  label: String.optional(),
  hidden: Boolean.optional(),
  require_acceptance: Boolean.optional(),
  is_required: Boolean.optional(),
  is_public: Boolean.optional(),
  revision: Number.optional(),
};

const BooleanElementDefinitionRuntype = Record({
  type: Literal('BooleanElement'),
  ...baseFields,
  default_value: Boolean,
  use_checkbox: Boolean,
});

export const ContentElementDefinitionRuntype = Record({
  type: Literal('ContentElement'),
  ...baseFields,
  text: String,
  format: String.optional(),
});

const FileUploadElementDefinitionRuntype = Record({
  type: Literal('FileUploadElement'),
  ...baseFields,
  image_only: Boolean,
  includeExpiry: Boolean.optional(),
});

const HeadingElementDefinition = Record({
  type: Literal('HeadingElement'),
  ...baseFields,
  text: String,
});

const LocationInputElementDefinition = Record({
  type: Literal('LocationInput'),
  ...baseFields,
  placeholder: String.Or(Null).Or(Undefined),
});

const MarketSelectorElementDefinitionRuntype = Record({
  type: Literal('MarketSelectorElement'),
  ...baseFields,
  disableSelectAll: Boolean.optional(),
  hiddenInForm: Boolean.optional(),
  calendarView: Boolean.optional(),
});

export const OptionRuntype = Record({
  id: String,
  name: String,
  deleted: Boolean.optional(),
});

export const OptionIdsRuntype = Array(String);
export const OptionsRuntype = Array(OptionRuntype);

export const MultichoiceElementDefinitionRuntype = Record({
  type: Literal('MultichoiceElement'),
  ...baseFields,
  options: Array(OptionRuntype),
  allow_multi: Boolean,
  condensed: Boolean.optional(),
  disable_select_all: Boolean.optional(),
});

const TextInputElementDefinitionRuntype = Record({
  type: Literal('TextElement'),
  ...baseFields,
  placeholder: String.Or(Null).optional(),
  multiline: Boolean.optional(),
  auto_linking: Boolean.optional(),
  field_validation: String.Or(Null).optional(),
});

const AddonElementDefinition = Record({
  type: Literal('AddonElement'),
  ...baseFields,
  disableSelectAll: Boolean.optional(),
});

const FormFieldDefinitionRuntype = Union(
  HeadingElementDefinition,
  ContentElementDefinitionRuntype,
  TextInputElementDefinitionRuntype,
  BooleanElementDefinitionRuntype,
  MarketSelectorElementDefinitionRuntype,
  MultichoiceElementDefinitionRuntype,
  FileUploadElementDefinitionRuntype,
  LocationInputElementDefinition,
  AddonElementDefinition,
);

export const SignupFormDefinitionRuntype = Record({
  id: String,
  name: String,
  enabled: Boolean.optional(),
  fields: Array(FormFieldDefinitionRuntype),
  groups: Array(Group).optional(),
  allow_renewal: Boolean.optional(),
  display_price: Boolean.optional(),
  payment_on_submit: PaymentMethodType.Or(Null).optional(),
  feeOption: String.optional(),
  feeValue: Number.optional(),
  show_preamble: Boolean.optional(),
  preamble_content: String.optional(),
  post_submit_content: String.optional(),
  send_post_submit_email: Boolean.optional(),
  post_submit_email_subject: String.Or(Null).optional(),
  post_submit_email_content: String.Or(Null).optional(),
  admin_notify: Boolean.optional(),
  filter_fields: Array(String).optional(),
  backgroundColor: RGBColorRuntype.optional(),
  params: Unknown.optional(),
  created_at: String.nullable().optional(),
  modified_at: String.optional(),
  revision: Number.optional(),
});

export type BooleanElementDefinition = Static<
  typeof BooleanElementDefinitionRuntype
>;
export type ContentElementDefinition = Static<
  typeof ContentElementDefinitionRuntype
>;
export type FileUploadElementDefinition = Static<
  typeof FileUploadElementDefinitionRuntype
>;
export type MarketSelectorElementDefinition = Static<
  typeof MarketSelectorElementDefinitionRuntype
>;
export type MultichoiceElementDefinition = Static<
  typeof MultichoiceElementDefinitionRuntype
>;
export type TextInputElementDefinition = Static<
  typeof TextInputElementDefinitionRuntype
>;
export type SignupFormDefinition = Static<typeof SignupFormDefinitionRuntype>;
export type FormFieldDefinition = Static<typeof FormFieldDefinitionRuntype>;
export type Option = Static<typeof OptionRuntype>;
